import { EventTimeline, MatrixClient, Room } from 'matrix-js-sdk/src/matrix';

import { getMeetingByRoomId } from 'api/meedioBackend/getMeetingByRoomId';
import { startMeeting } from 'api/meedioBackend/startMeeting';
import { MeetingResponse } from 'api/meedioBackend/types';
import { hasMeetingEndedEvent } from 'hooks/useScheduledMeetingTriggers/useScheduledMeetingTriggers';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';
import { isCallRoomMarkedInstant, isScheduledMeetingRecord } from 'utils/meetingInstantUtils';
import { getCurrentOrNextOccurrenceWindow, JOIN_PREROOM_THRESHOLD_MS } from 'utils/occurrenceWindow';

// 3-second backoff lets the organiser's notifyScheduledMeetingStarted win the
// emit race before non-organisers announce the meeting live.
const NON_ORGANISER_T1_BACKOFF_MS = 3000;
// Wait for our MESSENGER_CALL state write to sync back before checking whether we
// still hold leadership (last writer wins).
const STATE_SYNC_BACK_WAIT_MS = 1500;
const MEETING_STARTED_DEDUP_WINDOW_MS = 60_000;

const startNotifiedRooms = new Set<string>();

export const clearStartNotification = (matrixRoomId: string): void => {
  startNotifiedRooms.delete(matrixRoomId);
};

// Read-only view of the in-process start-dedup set. Used by
// useScheduledMeetingTriggers to drop redundant effect re-runs synchronously
// (BUG-018) before entering the async announce path.
export const isStartNotified = (matrixRoomId: string): boolean => startNotifiedRooms.has(matrixRoomId);

interface NotifyScheduledMeetingStartedParams {
  matrixClient: MatrixClient;
  matrixRoomId: string;
  // AC-B11: the trigger/announce loop supplies the meeting record it already
  // holds from the bulk store, so that path never re-fetches via
  // getMeetingByRoomId. AC-B13: the point-lookup callers (join, pre-room,
  // start-now, owner skip-lobby) have no MeetingResponse in scope and omit it —
  // those paths keep the existing getMeetingByRoomId fetch unchanged.
  meeting?: MeetingResponse;
}

const buildMessengerCallContent = (
  meeting: MeetingResponse,
  matrixRoomId: string,
  startedBy: string,
  startedAt: string
) => {
  const scheduledEndsAt = meeting.is_recurring
    ? meeting.current_occurrence_ends_at ?? meeting.ends_at
    : meeting.ends_at;
  return {
    call_room_id: matrixRoomId,
    started_at: startedAt,
    started_by: startedBy,
    meeting_id: meeting.id,
    meeting_title: meeting.room_name ?? '',
    is_scheduled: true,
    scheduled_ends_at: scheduledEndsAt,
  };
};

const isRestartAfterEnd = (messengerRoom: Room | null, matrixRoomId: string): boolean => {
  const events = messengerRoom?.getLiveTimeline?.().getEvents?.();
  if (!events || events.length === 0) return false;
  let lastStarted: number | null = null;
  let lastEnded: number | null = null;
  for (const event of events) {
    const type = event.getType();
    if (type !== CustomMeedioMatrixEvents.MEETING_STARTED && type !== CustomMeedioMatrixEvents.MEETING_ENDED) continue;
    if (event.getContent()?.meeting_room_id !== matrixRoomId) continue;
    const ts = event.getTs();
    if (type === CustomMeedioMatrixEvents.MEETING_STARTED) lastStarted = Math.max(lastStarted ?? 0, ts);
    else lastEnded = Math.max(lastEnded ?? 0, ts);
  }
  if (lastEnded === null) return false;
  return lastStarted === null || lastEnded > lastStarted;
};

/**
 * Has a `MEETING_STARTED` for this meeting room landed at or after `floorMs`?
 *
 * `floorMs` is an **absolute** floor timestamp — pass the occurrence window's
 * `startMs` to dedup across an entire scheduled occurrence (even multi-hour
 * meetings), or fall back to the legacy 60-second rolling window by omitting
 * it. The legacy default is preserved so callers that did not yet pass a
 * window keep their previous behaviour.
 *
 * Picking the right floor matters: a too-short window (e.g. the legacy 60s)
 * lets a duplicate `MEETING_STARTED` slip through whenever the original is
 * older than the window — see BUG-002. An occurrence-anchored floor catches
 * duplicates for the entire occurrence, no matter how long the meeting runs.
 */
export const hasRecentMeetingStartedEvent = (
  messengerRoom: Room | null,
  matrixRoomId: string,
  floorMs: number = Date.now() - MEETING_STARTED_DEDUP_WINDOW_MS
): boolean => {
  const events = messengerRoom?.getLiveTimeline?.().getEvents?.();
  if (!events || events.length === 0) return false;
  for (const event of events) {
    if (event.getType() !== CustomMeedioMatrixEvents.MEETING_STARTED) continue;
    if (event.getContent()?.meeting_room_id !== matrixRoomId) continue;
    if (event.getTs() >= floorMs) return true;
  }
  return false;
};

const claimedStateLeadership = async (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  ourEventId: string
): Promise<boolean> => {
  await new Promise((resolve) => setTimeout(resolve, STATE_SYNC_BACK_WAIT_MS));
  const messengerRoom = matrixClient.getRoom(messengerRoomId);
  const currentStateEvent = messengerRoom
    ?.getLiveTimeline()
    .getState(EventTimeline.FORWARDS)
    ?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL, '');
  const currentEventId = currentStateEvent?.getId();
  return currentEventId === ourEventId;
};

export const notifyScheduledMeetingStarted = async ({
  matrixClient,
  matrixRoomId,
  meeting: providedMeeting,
}: NotifyScheduledMeetingStartedParams): Promise<void> => {
  if (startNotifiedRooms.has(matrixRoomId)) return;

  startNotifiedRooms.add(matrixRoomId);

  // AC-B11: the trigger loop passes the meeting it already holds, so no fetch.
  // AC-B13: point-lookup callers omit it and keep the getMeetingByRoomId fetch.
  let meeting: MeetingResponse | null | undefined = providedMeeting;
  if (!meeting) {
    try {
      meeting = await getMeetingByRoomId(matrixRoomId);
    } catch (error) {
      startNotifiedRooms.delete(matrixRoomId);
      logger.warn('notifyScheduledMeetingStarted: getMeetingByRoomId failed', { error, matrixRoomId });
      throw error;
    }
  }

  if (!meeting || meeting.cancelled_at || !meeting.messenger_room_id) {
    startNotifiedRooms.delete(matrixRoomId);
    logger.warn('notifyScheduledMeetingStarted: no valid meeting to notify', {
      hasResponse: !!meeting,
      cancelled: meeting?.cancelled_at ?? null,
      hasMessengerRoom: !!meeting?.messenger_room_id,
      matrixRoomId,
    });
    return;
  }

  // BUG-005 Fix B: instant sessions use ActiveRoom deferred start — never scheduled copy.
  if (!isScheduledMeetingRecord(meeting) || isCallRoomMarkedInstant(matrixClient, matrixRoomId)) {
    startNotifiedRooms.delete(matrixRoomId);
    return;
  }

  const window = getCurrentOrNextOccurrenceWindow(meeting);
  if (window && Date.now() >= window.endMs) {
    startNotifiedRooms.delete(matrixRoomId);
    logger.warn('notifyScheduledMeetingStarted: occurrence window already closed — refusing /start', {
      meetingId: meeting.id,
      endMs: window.endMs,
    });
    return;
  }

  // BUG-005: occurrence already closed by a MEETING_ENDED on the timeline. Re-arming
  // a fresh MEETING_STARTED would resurrect the just-ended row. Mark the Set so
  // concurrent retries also bail. The end-event check uses the occurrence's
  // start as the floor — same anchor as the rest of the BUG-002/005 dedup.
  if (
    meeting.messenger_room_id &&
    window &&
    hasMeetingEndedEvent(matrixClient, meeting.messenger_room_id, matrixRoomId, window.startMs)
  ) {
    startNotifiedRooms.add(matrixRoomId);
    return;
  }

  const currentUserId = matrixClient.getUserId();
  if (meeting.created_by_mxid !== currentUserId) {
    startNotifiedRooms.delete(matrixRoomId);
    logger.warn('notifyScheduledMeetingStarted: not the organiser — skipping notification emit', {
      created_by_mxid: meeting.created_by_mxid,
      current_user: currentUserId,
      matrixRoomId,
    });
    return;
  }

  let startedNow = false;
  let backendStartedAt: string | null = null;
  try {
    const result = await startMeeting(meeting.id);
    startedNow = result.started_now;
    // BUG-058: the IS backend records the per-occurrence start time once on the
    // first /start call and returns the same value on all subsequent calls
    // (started_now: false). This is the authoritative "when someone actually
    // pressed Start" timestamp — correct for early starts, on-time starts, and
    // idempotent for multiple clients. Store it here so every event payload uses
    // the same backend-confirmed value instead of a local Date.now() estimate.
    backendStartedAt = result.started_at ?? null;
  } catch (error) {
    startNotifiedRooms.delete(matrixRoomId);
    logger.warn('notifyScheduledMeetingStarted: startMeeting failed', {
      error,
      meetingId: meeting.id,
    });
    throw error;
  }

  const messengerRoomId = meeting.messenger_room_id;
  const title = meeting.room_name ?? '';
  // BUG-058: prefer the IS backend's per-occurrence started_at (set once, idempotent).
  // Fall back to min(window.startMs, now) — the BUG-054 heuristic — when the
  // backend value is unexpectedly absent (shouldn't happen, defensive fallback only).
  const startedAt =
    backendStartedAt ??
    (window ? new Date(Math.min(window.startMs, Date.now())).toISOString() : new Date().toISOString());
  const startedBy = matrixClient.getUserId() ?? '';

  const messengerRoom = matrixClient.getRoom(messengerRoomId);
  const existingCallState = messengerRoom
    ?.getLiveTimeline()
    .getState(EventTimeline.FORWARDS)
    ?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL, '');
  const existingCallRoomId = existingCallState?.getContent()?.call_room_id as string | undefined;
  const isAlreadyLive = existingCallRoomId === matrixRoomId;

  // Occurrence-window dedup (BUG-002 / BUG-005): use the meeting's current occurrence
  // start as the floor for "is there already a MEETING_STARTED on the timeline?".
  // BUG-005 widens it back by JOIN_PREROOM_THRESHOLD_MS so an early-start (organiser
  // clicked "Start now" up to 15 min before scheduled_at) is still inside the dedup
  // window and a subsequent emit is suppressed.
  // The `startedNow` gate was removed (main branch fix): gating on it was brittle
  // because /start returns `started_now: false` in dev after reload and on StrictMode
  // double-invocations. The `hasMeetingEndedEventForOccurrence` guard above already
  // handles the restart-after-end case, so we simply gate on `!isAlreadyLive`.
  const startedFloorMs = window
    ? window.startMs - JOIN_PREROOM_THRESHOLD_MS
    : Date.now() - MEETING_STARTED_DEDUP_WINDOW_MS;

  if (!isAlreadyLive) {
    // isRestartAfterEnd catches the case where the last MEETING_ENDED on the
    // timeline follows the last MEETING_STARTED (regardless of occurrence window).
    // hasMeetingEndedEventForOccurrence above only looks inside the window floor,
    // so very-old end events (before current window.startMs) are only caught here.
    if (isRestartAfterEnd(messengerRoom ?? null, matrixRoomId)) {
      startNotifiedRooms.delete(matrixRoomId);
      return;
    }

    // BUG-016: another meeting already owns the MESSENGER_CALL singleton. Writing
    // our state would evict the current primary on every client and reset its timer.
    // Skip the state write but still emit MEETING_STARTED so the chat row appears,
    // and release the dedup entry so this meeting claims the slot once it frees.
    const anotherMeetingIsPrimary = !!existingCallRoomId;
    if (anotherMeetingIsPrimary) startNotifiedRooms.delete(matrixRoomId);

    // BUG-005 (startedFloorMs): use the occurrence-window floor so duplicate
    // MEETING_STARTED emits from early-started occurrences are still suppressed.
    const peerAlreadyStarted = hasRecentMeetingStartedEvent(messengerRoom ?? null, matrixRoomId, startedFloorMs);

    let ourStateEventId: string | null = null;
    if (!anotherMeetingIsPrimary) {
      try {
        const result = await matrixClient.sendStateEvent(
          messengerRoomId,
          CustomMeedioMatrixEvents.MESSENGER_CALL,
          buildMessengerCallContent(meeting, matrixRoomId, startedBy, startedAt),
          ''
        );
        ourStateEventId = result?.event_id ?? null;
      } catch (error) {
        logger.warn('notifyScheduledMeetingStarted: MESSENGER_CALL state emit failed', {
          error,
          meetingId: meeting.id,
          startedNow,
        });
      }
    }

    if (peerAlreadyStarted) return;

    if (ourStateEventId) {
      const weWon = await claimedStateLeadership(matrixClient, messengerRoomId, ourStateEventId);
      if (!weWon) return;
    }

    if (hasRecentMeetingStartedEvent(matrixClient.getRoom(messengerRoomId) ?? null, matrixRoomId, startedFloorMs)) {
      return;
    }

    try {
      await matrixClient.sendEvent(messengerRoomId, CustomMeedioMatrixEvents.MEETING_STARTED, {
        meeting_room_id: matrixRoomId,
        started_at: startedAt,
        started_by: startedBy,
        meeting_id: meeting.id,
        title,
      });
    } catch (error) {
      logger.warn('notifyScheduledMeetingStarted: MEETING_STARTED timeline emit failed', {
        error,
        meetingId: meeting.id,
        startedNow,
      });
    }
  }
};

export const announceMeetingLive = async ({
  matrixClient,
  matrixRoomId,
  meeting: providedMeeting,
}: NotifyScheduledMeetingStartedParams): Promise<void> => {
  if (startNotifiedRooms.has(matrixRoomId)) return;

  await new Promise((resolve) => setTimeout(resolve, NON_ORGANISER_T1_BACKOFF_MS));
  if (startNotifiedRooms.has(matrixRoomId)) return;

  // AC-B11: the trigger loop passes the meeting it already holds, so no fetch.
  // AC-B13: point-lookup callers omit it and keep the getMeetingByRoomId fetch.
  let meeting: MeetingResponse | null | undefined = providedMeeting;
  if (!meeting) {
    try {
      meeting = await getMeetingByRoomId(matrixRoomId);
    } catch (error) {
      logger.warn('announceMeetingLive: getMeetingByRoomId failed', { error, matrixRoomId });
      return;
    }
  }

  if (!meeting || meeting.cancelled_at || !meeting.messenger_room_id) {
    logger.warn('announceMeetingLive: no valid meeting to announce', {
      hasResponse: !!meeting,
      cancelled: meeting?.cancelled_at ?? null,
      hasMessengerRoom: !!meeting?.messenger_room_id,
      matrixRoomId,
    });
    return;
  }

  // BUG-005 Fix B: instant call rooms must not get scheduled MEETING_STARTED copy.
  if (!isScheduledMeetingRecord(meeting) || isCallRoomMarkedInstant(matrixClient, matrixRoomId)) {
    return;
  }

  const announceWindow = getCurrentOrNextOccurrenceWindow(meeting);
  if (announceWindow && Date.now() >= announceWindow.endMs) {
    logger.warn('announceMeetingLive: occurrence window already closed — skipping announce', {
      meetingId: meeting.id,
      endMs: announceWindow.endMs,
    });
    return;
  }

  // BUG-005 / BUG-002: mirror fireScheduledStart — widen the end floor by
  // JOIN_PREROOM_THRESHOLD_MS so an early host end before scheduled_at is visible.
  const endedGuardFloorMs = announceWindow
    ? announceWindow.startMs - JOIN_PREROOM_THRESHOLD_MS
    : Date.now() - MEETING_STARTED_DEDUP_WINDOW_MS;
  if (
    meeting.messenger_room_id &&
    hasMeetingEndedEvent(matrixClient, meeting.messenger_room_id, matrixRoomId, endedGuardFloorMs)
  ) {
    startNotifiedRooms.add(matrixRoomId);
    return;
  }
  if (announceWindow && meeting.ended_at) {
    const endedAtMs = Date.parse(meeting.ended_at);
    if (endedAtMs >= endedGuardFloorMs && endedAtMs < announceWindow.endMs) {
      startNotifiedRooms.add(matrixRoomId);
      return;
    }
  }

  const messengerRoomId = meeting.messenger_room_id;
  const messengerRoom = matrixClient.getRoom(messengerRoomId);
  const existingCallState = messengerRoom
    ?.getLiveTimeline()
    .getState(EventTimeline.FORWARDS)
    ?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL, '');
  const existingCallRoomId = existingCallState?.getContent()?.call_room_id as string | undefined;
  if (existingCallRoomId === matrixRoomId) {
    startNotifiedRooms.add(matrixRoomId);
    return;
  }

  // BUG-016: another meeting already owns the MESSENGER_CALL singleton. Writing
  // our state would evict the current primary on every client and reset its
  // timer. Skip the state write but still emit MEETING_STARTED so the chat row
  // appears, and leave this room OUT of startNotifiedRooms so it is retried as
  // primary once the slot frees.
  const anotherMeetingIsPrimary = !!existingCallRoomId;
  if (!anotherMeetingIsPrimary) startNotifiedRooms.add(matrixRoomId);

  // Occurrence-window dedup (BUG-002/BUG-005): see comment in notifyScheduledMeetingStarted.
  // Widened by JOIN_PREROOM_THRESHOLD_MS to cover early-started occurrences.
  const announceFloorMs = announceWindow
    ? announceWindow.startMs - JOIN_PREROOM_THRESHOLD_MS
    : Date.now() - MEETING_STARTED_DEDUP_WINDOW_MS;
  const peerAlreadyStarted = hasRecentMeetingStartedEvent(messengerRoom ?? null, matrixRoomId, announceFloorMs);

  const title = meeting.room_name ?? '';
  // BUG-058: if the organiser already ran notifyScheduledMeetingStarted and set
  // MESSENGER_CALL.started_at (containing the backend-authoritative /start response
  // value), reuse it so all clients agree on the same timer basis.
  // If the organiser hasn't announced yet (this client is the backup announce),
  // fall back to the BUG-054 heuristic: min(occurrence.startMs, now).
  const existingStartedAt = existingCallState?.getContent()?.started_at as string | undefined;
  const startedAt =
    existingStartedAt ??
    (announceWindow ? new Date(Math.min(announceWindow.startMs, Date.now())).toISOString() : new Date().toISOString());
  // Always anchor started_by to the organiser — never the caller — so every
  // client derives isHost the same way regardless of who fired the announce.
  const startedBy = meeting.created_by_mxid ?? '';

  let ourStateEventId: string | null = null;
  if (!anotherMeetingIsPrimary) {
    try {
      const result = await matrixClient.sendStateEvent(
        messengerRoomId,
        CustomMeedioMatrixEvents.MESSENGER_CALL,
        buildMessengerCallContent(meeting, matrixRoomId, startedBy, startedAt),
        ''
      );
      ourStateEventId = result?.event_id ?? null;
    } catch (error) {
      logger.warn('announceMeetingLive: MESSENGER_CALL state emit failed', {
        error,
        meetingId: meeting.id,
      });
      startNotifiedRooms.delete(matrixRoomId);
      return;
    }
  }

  if (peerAlreadyStarted) return;

  if (ourStateEventId) {
    const weWon = await claimedStateLeadership(matrixClient, messengerRoomId, ourStateEventId);
    if (!weWon) return;
  }

  if (hasRecentMeetingStartedEvent(matrixClient.getRoom(messengerRoomId) ?? null, matrixRoomId, announceFloorMs)) {
    return;
  }

  try {
    await matrixClient.sendEvent(messengerRoomId, CustomMeedioMatrixEvents.MEETING_STARTED, {
      meeting_room_id: matrixRoomId,
      started_at: startedAt,
      started_by: startedBy,
      meeting_id: meeting.id,
      title,
    });
  } catch (error) {
    logger.warn('announceMeetingLive: MEETING_STARTED timeline emit failed', {
      error,
      meetingId: meeting.id,
    });
  }
};
